import { Dropdown } from 'antd';
import { CaretDownOutlined } from '@ant-design/icons';
import { FileUser, UserMapping } from '../../../../types/Import';
import { WorkspaceAccessResponse } from '../../../../api/workspace';
import { getUserProfilePictureUrl } from '../../../../common/userHelper';

interface ImportMappingUsersProps {
  userMappings: UserMapping[];
  fileUsers: FileUser[];
  workspaceUsers: WorkspaceAccessResponse[] | undefined;
  onUserMappingChange: (workspaceUserId: number, fileUserName: string | null) => void;
}

export const ImportMappingUsers = ({
  userMappings,
  fileUsers,
  workspaceUsers,
  onUserMappingChange,
}: ImportMappingUsersProps) => {
  if (!workspaceUsers || workspaceUsers.length === 0) {
    return (
      <div className="bg-neutral-100 rounded-lg p-4 mb-4">
        <div className="text-brand-grey-dark text-sm font-semibold mb-3">Users</div>
        <div className="text-neutral-700 text-sm py-2">No workspace members found</div>
      </div>
    );
  }

  if (!fileUsers || fileUsers.length === 0) {
    return null;
  }

  // Derive avatar URLs from the authoritative workspace members (matched by id),
  // falling back to the mapping's own value, then to the initials placeholder.
  // Both paths must go through getUserProfilePictureUrl: the API sends
  // workspaceUserAvatar as a raw storage path, not a resolvable URL.
  const avatarByUserId = new Map(
    workspaceUsers.map((wu) => [wu.User.id, getUserProfilePictureUrl(wu.User.profilePicturePath ?? null)])
  );

  const buildMenuItems = (workspaceUserId: number) => [
    {
      key: 'none',
      label: 'Choose',
      onClick: () => onUserMappingChange(workspaceUserId, null),
    },
    ...fileUsers.map((u) => ({
      key: u.name,
      label: `${u.name} (${u.occurrences} task${u.occurrences === 1 ? '' : 's'})`,
      onClick: () => onUserMappingChange(workspaceUserId, u.name),
    })),
  ];

  return (
    <div className="bg-neutral-100 rounded-lg p-4 mb-4">
      <div className="text-brand-grey-dark text-sm font-semibold mb-2">Users</div>
      <div className="flex items-center pb-2">
        <span className="text-neutral-700 text-xs flex-1">Taskonio</span>
        <span className="text-neutral-700 text-xs">Users from your file</span>
      </div>

      <div className="flex flex-col">
        {userMappings.map((mapping) => {
          const isMapped = !!mapping.fileUserName;
          const avatarUrl =
            avatarByUserId.get(mapping.workspaceUserId) ??
            getUserProfilePictureUrl(mapping.workspaceUserAvatar) ??
            null;

          return (
            <div
              key={mapping.workspaceUserId}
              className="flex items-center justify-between py-2 border-b border-neutral-200 last:border-b-0"
            >
              <div className="flex items-center gap-2">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" className="w-6 h-6 rounded-full" />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-neutral-300 flex items-center justify-center text-xs text-neutral-700">
                    {(mapping.workspaceUserName ?? '?').charAt(0).toUpperCase()}
                  </div>
                )}
                <span className="text-brand-grey-dark text-sm">{mapping.workspaceUserName ?? 'Unknown'}</span>
              </div>
              <Dropdown
                menu={{ items: buildMenuItems(mapping.workspaceUserId), style: { maxHeight: 400, overflowY: 'auto' } }}
                trigger={['click']}
              >
                <button className="flex items-center gap-1.5 bg-transparent border-none cursor-pointer py-0.5">
                  <span className={`text-sm ${isMapped ? 'text-brand-grey-dark' : 'text-neutral-500'}`}>
                    {mapping.fileUserName ?? 'Choose'}
                  </span>
                  <CaretDownOutlined className="text-neutral-500 text-[10px]" />
                </button>
              </Dropdown>
            </div>
          );
        })}
      </div>
    </div>
  );
};
